import { motion } from "framer-motion";
import { Eye, ArrowDownRight, MoveRight, RotateCcw } from "lucide-react";
import { HOUSE } from "@/constants/testIds";

const STEPS = [
  {
    icon: Eye,
    label: "Notice it.",
    body: "A visitor recognizes the moment — the need for a place to set something down.",
  },
  {
    icon: ArrowDownRight,
    label: "Deploy it.",
    body: "A press. The concealed torque hinge releases. The platform lowers with controlled, elegant movement to ninety degrees.",
  },
  {
    icon: MoveRight,
    label: "Use it.",
    body: "Whatever is being carried finds its temporary place — respected, elevated, considered.",
  },
  {
    icon: RotateCcw,
    label: "Close it.",
    body: "The platform returns to the wall. The environment resumes its quiet, architectural composure.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const Experience = () => {
  return (
    <section
      id="experience"
      data-testid={HOUSE.sectionExperience}
      className="relative bg-walnut-grain py-28 md:py-36 border-t border-[var(--border-dark)]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.p
          {...fadeUp}
          className="font-sans text-[11px] uppercase tracking-[0.5em] text-[var(--gold-light)] mb-8"
        >
          The Abike Experience
        </motion.p>
        <motion.h2
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.08 }}
          className="font-serif text-[var(--ivory)] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light max-w-3xl"
        >
          Four gestures.
          <br />
          <span className="italic text-[var(--gold-light)]">One quiet ritual.</span>
        </motion.h2>

        <div className="mt-20 grid md:grid-cols-4 gap-px bg-[var(--border-dark)] border border-[var(--border-dark)]">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="bg-[var(--walnut-deep)] p-8 md:p-10 min-h-[280px] flex flex-col"
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="font-serif text-[var(--gold-light)] text-3xl">
                    0{i + 1}
                  </span>
                  <div className="w-10 h-10 border border-[var(--gold)]/40 flex items-center justify-center text-[var(--gold-light)]">
                    <Icon size={16} strokeWidth={1.4} />
                  </div>
                </div>
                <h3 className="font-serif text-[var(--ivory)] text-2xl md:text-3xl tracking-tight mb-4">
                  {s.label}
                </h3>
                <p className="font-sans text-[var(--stone)]/80 text-sm md:text-base leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
